import React, {Component} from 'react';
import {compose, withProps} from 'recompose';
import {withScriptjs, withGoogleMap, GoogleMap} from 'react-google-maps';

import {Project} from '../../types';
import ProjectMarker from '../ProjectMarker';
import {TERRITORY_ACTIVE_STYLE, TERRITORY_STYLE} from '../ProjectMarker/constants';

import styles from './styles.module.scss';
import mapStyles from './map-styles';
import {GOOGLE_MAPS_API_KEY, DEFAULT_ZOOM, CONTROL_SIZE} from './constants';
import {frame, LatLng} from './framing';
import {BaseMap, readBaseMap} from '~components/BaseMapSelector/baseMap';
import {Route} from "~services/RoutesService";

// Territories carry an Acción count; Acciones carry their territories.
const isTerritory = (element: Project | Route): element is Route => (element as Project).territories === undefined;

interface Props {
    projects: Project[] | Route[];
    route?: Route;
    loading?: boolean;
    hoveredProjectId?: number;
    selectedProjectId?: number;
    isSingle?: boolean;
    zoom?: number;
    selectable?: boolean;
    baseMap?: BaseMap;
}

// Our own controls (zoom only); the base map comes from BaseMapSelector.
const CONTROLS = {
    streetViewControl: false,
    scaleControl: false,
    mapTypeControl: false,
    panControl: false,
    rotateControl: false,
    fullscreenControl: false,
    controlSize: CONTROL_SIZE
};

// Constant objects: react-google-maps only calls setOptions when the base map actually changes.
const BASE_MAP_OPTIONS: Record<BaseMap, {mapTypeId: string, options: object}> = {
    terrain: {mapTypeId: 'terrain', options: {...CONTROLS, styles: []}},
    light: {mapTypeId: 'roadmap', options: {...CONTROLS, styles: mapStyles}},
    satellite: {mapTypeId: 'satellite', options: {...CONTROLS, styles: []}}
};

class Map extends Component<Props> {
    mapRef = {getBounds: () => ({})};

    // react-google-maps re-centers whenever `center` is a new object: reuse it while the target is the same,
    // so hovering a card doesn't undo the user's panning.
    view?: {key: string, center: LatLng};

    renderProjectMarker = (project: Project | Route) => {
        const {hoveredProjectId, selectedProjectId, selectable} = this.props;
        const territoryStyle = isTerritory(project) ? {activeSettings: TERRITORY_ACTIVE_STYLE, inactiveSettings: TERRITORY_STYLE} : {};

        return (
            <ProjectMarker
                key={project.id}
                project={project}
                hovered={hoveredProjectId === project.id}
                selected={selectedProjectId === project.id}
                selectable={selectable || false}
                visible
                {...territoryStyle}
            />
        );

    };

    renderRouteMarker = (route: Route) => {
        return (
            <ProjectMarker
                key={route.id}
                project={route}
                hovered={false}
                selected={false}
                visible={true}
                activeSettings={TERRITORY_STYLE}
                inactiveSettings={TERRITORY_STYLE}
            />
        );
    }

    stableCenter = (center: LatLng, zoom: number) => {
        const key = `${center.lat},${center.lng},${zoom}`;
        if (this.view?.key !== key) {
            this.view = {key, center};
        }
        return this.view.center;
    }

    setMapRef = map => (this.mapRef = map);

    render() {
        const {projects, route,  loading,  zoom} = this.props;
        let defaultCenter = {lat: -26, lng: -66};
        let useZoom = zoom || DEFAULT_ZOOM;
        const projectOrRoute = projects?.[0];
        const isSingle = projects?.length === 1;
        if (projectOrRoute?.hasOwnProperty('dynamicCenter')) {
            const project = projectOrRoute as Project;
            const dynamicCenter = project?.dynamicCenter;
            if (isSingle && dynamicCenter) {
                const lat = Number.parseFloat(dynamicCenter.latitude.toString());
                const lng = Number.parseFloat(dynamicCenter.longitude.toString());
                defaultCenter = {lat, lng};
            }
        }
        if (route && route.ajusteMapa) {
            const lat = Number.parseFloat(route.ajusteMapa.latitude.toString());
            const lng = Number.parseFloat(route.ajusteMapa.longitude.toString());
            defaultCenter = {lat, lng};
            useZoom = route.ajusteMapa.zoom;
        } else if (route || (!isSingle && !zoom)) {
            // A territory without a saved framing, or a page of Acciones: fit the Acciones (territories have none).
            const framing = frame((projects as Project[]).filter(project => project.dynamicCenter).map(({dynamicCenter}) => ({
                lat: Number.parseFloat(dynamicCenter.latitude.toString()),
                lng: Number.parseFloat(dynamicCenter.longitude.toString())
            })));
            if (framing) {
                defaultCenter = framing.center;
                useZoom = framing.zoom;
            }
        }
        defaultCenter = this.stableCenter(defaultCenter, useZoom);
        // Maps without a selector (the Ficha's modal) follow the viewer's last choice.
        const baseMap = this.props.baseMap || readBaseMap();

        return (
            <GoogleMap
                ref={this.setMapRef}
                defaultZoom={useZoom}
                defaultCenter={defaultCenter}
                zoom={useZoom}
                center={defaultCenter}
                mapTypeId={BASE_MAP_OPTIONS[baseMap].mapTypeId}
                options={BASE_MAP_OPTIONS[baseMap].options}
            >
                {!loading && !isSingle && projects?.map(this.renderProjectMarker)}
                {!loading && route && this.renderRouteMarker(route)}
                {isSingle && <ProjectMarker project={projects[0] as Project} hovered visible/>}
            </GoogleMap>
        );
    }
}

export default compose(
    withProps(ownerProps => ({
        googleMapURL: `https://maps.googleapis.com/maps/api/js?v=3.exp&key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,drawing,places`,
        loadingElement: <div className={styles.loading}/>,
        containerElement: (
            <></>
        ),
        mapElement: <div className={styles.map}/>
    })),
    withScriptjs,
    withGoogleMap
)(Map);
